// src/pages/admin/CreateScoringSessionPage.js
// Updated: 11/09/2025 09:24 PM EST
// --- COPY-PASTE THIS ENTIRE FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate, Link, useLocation } from 'react-router-dom';
// --- 1. REMOVED unused 'EyeOff' and 'Eye' icons ---
import { Plus, Trash2, ArrowLeft, ToggleLeft, ToggleRight, Check } from 'lucide-react';

export default function CreateScoringSessionPage() {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [useSimplified, setUseSimplified] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  // --- State for authorization ---
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  // ---

  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if we are in Edit Mode, check authorization, and fetch existing data
  useEffect(() => {
    // Wait until we know who the user is
    if (!userData || !currentUser) {
      setIsCheckingAuth(true);
      return;
    }

    const query = new URLSearchParams(location.search);
    const editId = query.get('edit');
    const isGlobalAdmin = userData.role === 'admin';

    const fetchSession = async (sessionId) => {
      try {
        const sessionRef = doc(db, 'scoringSessions', sessionId);
        const sessionSnap = await getDoc(sessionRef);

        if (sessionSnap.exists() && sessionSnap.data().orgId === userData.orgId) {
          const data = sessionSnap.data();

          // --- PERMISSION CHECK FOR EDIT ---
          const isSponsor = data.sessionSponsors?.includes(currentUser.uid);
          if (!isGlobalAdmin && !isSponsor) {
            throw new Error("Access Denied. You must be an admin or session sponsor to edit this session.");
          }
          // --- END PERMISSION CHECK ---

          setIsAuthorized(true); // User can be here
          setName(data.name);
          setDescription(data.description);
          setUseSimplified(!!data.useSimplifiedRubric);
          setIsAnonymous(!!data.isAnonymous);
          const categoriesWithIds = data.categories.map((cat, index) => ({
            ...cat,
            id: cat.id || Date.now() + index,
            criteria: data.useSimplifiedRubric ? undefined : cat.criteria?.map((crit, cIndex) => ({
              ...crit,
              id: crit.id || Date.now() + index + cIndex + 100,
            }))
          }));
          setCategories(categoriesWithIds);
        } else {
          throw new Error("Session not found or access denied.");
        }
      } catch (e) {
        setError(e.message || "Failed to load session for editing.");
        setIsAuthorized(false); // Failed, so not authorized
      } finally {
        setIsPageLoading(false);
        setIsCheckingAuth(false);
      }
    };

    if (editId) {
      // This is an EDIT operation
      setIsEditing(true);
      fetchSession(editId);
    } else {
      // This is a CREATE operation, only global admins can do this
      if (isGlobalAdmin) {
        setIsAuthorized(true);
        // Initialize for new session
        const initialCategory = {
          id: Date.now(), name: '', maxPoints: 10, simplifiedCriteria: '',
          criteria: [{ id: Date.now() + 1, name: '', maxPoints: 10, weight: 1 }]
        };
        setCategories([initialCategory]);
      } else {
        setError("Access Denied. You must be an admin to create a new session.");
        setIsAuthorized(false);
      }
      setIsPageLoading(false);
      setIsCheckingAuth(false);
    }
  }, [location.search, userData, currentUser]); // Rerun if user or query param changes


  // --- Category Handlers (No changes) ---
  const addCategory = () => {
    const newCategory = {
      id: Date.now(),
      name: '',
      maxPoints: 10,
      simplifiedCriteria: useSimplified ? '' : undefined,
      criteria: !useSimplified ? [{ id: Date.now() + 1, name: '', maxPoints: 10, weight: 1 }] : undefined
    };
    setCategories([...categories, newCategory]);
  };
  const removeCategory = (categoryId) => {
    setCategories(categories.filter(cat => cat.id !== categoryId));
  };
  const handleCategoryChange = (categoryId, field, value) => {
    setCategories(categories.map(cat =>
      cat.id === categoryId ? { ...cat, [field]: value } : cat
    ));
  };
  const addCriterion = (categoryId) => {
    setCategories(categories.map(cat =>
      cat.id === categoryId ? { ...cat, criteria: [...cat.criteria, { id: Date.now(), name: '', maxPoints: 10, weight: 1 }] } : cat
    ));
  };
  const removeCriterion = (categoryId, criterionId) => {
    setCategories(categories.map(cat =>
      cat.id === categoryId ? { ...cat, criteria: cat.criteria.filter(crit => crit.id !== criterionId) } : cat
    ));
  };
  const handleCriterionChange = (categoryId, criterionId, field, value) => {
    const numericValue = (field === 'maxPoints' || field === 'weight') ? Number(value) : value;
    setCategories(categories.map(cat =>
      cat.id === categoryId ? {
        ...cat,
        criteria: cat.criteria.map(crit =>
          crit.id === criterionId ? { ...crit, [field]: numericValue } : crit
        )
      } : cat
    ));
  };
  // --- End Category Handlers ---


  // --- Form Submission (No changes) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthorized) return; // Final check
    setError('');
    setIsLoading(true);

    if (name.trim() === '') { setError('Session name is required.'); setIsLoading(false); return; }
    let isValid = true;
    if (!useSimplified) {
      if (categories.some(cat => cat.name.trim() === '' || !cat.criteria || cat.criteria.length === 0 || cat.criteria.some(crit => crit.name.trim() === '' || crit.maxPoints <= 0))) {
        isValid = false;
      }
    } else {
      if (categories.some(cat => cat.name.trim() === '' || cat.maxPoints <= 0)) {
        isValid = false;
      }
    }
    if (!isValid) {
      setError('All categories/criteria must have names, and max points must be greater than 0.');
      setIsLoading(false);
      return;
    }

    const finalCategories = categories.map(({ id, ...cat }) => {
      if (useSimplified) {
        const { criteria, ...simpleCat } = cat;
        return simpleCat;
      } else {
        const { simplifiedCriteria, maxPoints, ...detailedCat } = cat;
        return {
          ...detailedCat,
          criteria: detailedCat.criteria.map(({ id, ...crit }) => crit)
        };
      }
    });

    const sessionData = {
      orgId: userData.orgId,
      createdBy: currentUser.uid,
      name: name,
      description: description,
      useSimplifiedRubric: useSimplified,
      isAnonymous: isAnonymous,
      categories: finalCategories,
      ...(!isEditing && { sessionSponsors: [] })
    };

    try {
      if (isEditing) {
        const editId = new URLSearchParams(location.search).get('edit');
        await updateDoc(doc(db, 'scoringSessions', editId), {
          name: sessionData.name,
          description: sessionData.description,
          useSimplifiedRubric: sessionData.useSimplifiedRubric,
          isAnonymous: sessionData.isAnonymous,
          categories: sessionData.categories
        });
        navigate(`/admin/scoring/${editId}/manage`);
      } else {
        await addDoc(collection(db, 'scoringSessions'), { ...sessionData, status: 'setup', createdAt: serverTimestamp() });
        navigate('/scoring');
      }
    } catch (err) {
      console.error('Error saving scoring session:', err);
      setError('Failed to save session. Please check your network.');
    } finally {
      setIsLoading(false);
    }
  };


  // --- Security Checks (No changes) ---
  if (isCheckingAuth || isPageLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
        <p className="ml-4 text-gray-700">Loading session data...</p>
      </div>
    );
  }
  if (!isAuthorized) {
    return (
      <div>
        <Link
          to={isEditing ? `/admin/scoring/${new URLSearchParams(location.search).get('edit')}/manage` : "/scoring"}
          className="mb-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to {isEditing ? 'Management' : 'Scoring'}
        </Link>
        <p className="mt-4 text-red-600 font-semibold">{error || "Access Denied. You must be an admin or session sponsor to create/edit scoring sessions."}</p>
      </div>
    );
  }
  if (categories.length === 0 && !isPageLoading) {
    const initialCategory = { id: Date.now(), name: '', maxPoints: 10, simplifiedCriteria: '', criteria: [{ id: Date.now() + 1, name: '', maxPoints: 10, weight: 1 }] };
    setCategories([initialCategory]);
  }
  // --- End Security Checks ---


  return (
    <div className="rounded-lg bg-white p-6 shadow-lg">
      <Link
        to={isEditing ? `/admin/scoring/${new URLSearchParams(location.search).get('edit')}/manage` : "/scoring"}
        className="mb-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to {isEditing ? 'Management' : 'Scoring'}
      </Link>
      <h1 className="text-3xl font-bold text-gray-900">{isEditing ? 'Edit Scoring Session' : 'Create New Scoring Session'}</h1>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        {/* Session Details (No changes) */}
        <div className="space-y-4 rounded-md border p-4">
          <h2 className="text-xl font-semibold">Session Details</h2>
          <p className="text-sm text-gray-500">Note: Name and Description are always editable.</p>
          <div>
            <label htmlFor="session-name" className="block text-sm font-medium text-gray-700">Session Name</label>
            <input type="text" id="session-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label htmlFor="session-desc" className="block text-sm font-medium text-gray-700">Description (Optional)</label>
            <textarea id="session-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows="3" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"></textarea>
          </div>
        </div>

        {/* Toggles (No changes) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-md border p-4">
            <h2 className="text-xl font-semibold mb-2">Rubric Type</h2>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
              <span className="text-sm font-medium text-gray-700">
                Mode: <span className="font-bold text-blue-600">{useSimplified ? 'Simplified' : 'Detailed'}</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setUseSimplified(!useSimplified);
                  setCategories(categories.map(cat => ({
                    ...cat,
                    simplifiedCriteria: !useSimplified ? '' : undefined,
                    criteria: !useSimplified ? undefined : (cat.criteria || [{ id: Date.now() + 1, name: '', maxPoints: 10, weight: 1 }])
                  })));
                }}
                disabled={isEditing}
                className={`flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {useSimplified ? <ToggleRight className="h-6 w-6 mr-1" /> : <ToggleLeft className="h-6 w-6 mr-1" />}
                Switch to {useSimplified ? 'Detailed' : 'Simplified'}
              </button>
            </div>
            {isEditing && <p className="mt-2 text-xs text-red-500">Rubric type cannot be changed after creation.</p>}
          </div>
          <div className="rounded-md border p-4">
            <h2 className="text-xl font-semibold mb-2">Anonymity</h2>
            <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
              <span className="text-sm font-medium text-gray-700">
                Mode: <span className="font-bold text-blue-600">{isAnonymous ? 'Anonymous' : 'Public'}</span>
              </span>
              <button
                type="button"
                onClick={() => setIsAnonymous(!isAnonymous)}
                disabled={isEditing}
                className={`flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium ${isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isAnonymous ? <ToggleRight className="h-6 w-6 mr-1" /> : <ToggleLeft className="h-6 w-6 mr-1" />}
                Switch to {isAnonymous ? 'Public' : 'Anonymous'}
              </button>
            </div>
            {isEditing && <p className="mt-2 text-xs text-red-500">Anonymity cannot be changed after creation.</p>}
          </div>
        </div>

        {/* Categories & Criteria (No changes) */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Scoring Rubric Definition</h2>
          {categories.map((category, catIndex) => (
            <div key={category.id} className="space-y-4 rounded-md border border-gray-300 p-4">
              <div className="flex items-center justify-between">
                <input
                  type="text"
                  placeholder={`Category ${catIndex + 1} Name (e.g., Technical Merit)`}
                  value={category.name}
                  onChange={(e) => handleCategoryChange(category.id, 'name', e.target.value)}
                  required
                  className="flex-grow rounded-md border-gray-300 text-lg font-medium shadow-sm"
                />
                {useSimplified && (
                  <div className="ml-4 w-32 flex-shrink-0">
                    <label className="block text-xs text-gray-500">Max Points</label>
                    <input
                      type="number"
                      min="1"
                      value={category.maxPoints || 1}
                      onChange={(e) => handleCategoryChange(category.id, 'maxPoints', Number(e.target.value))}
                      required
                      className="w-full rounded-md border-gray-300 shadow-sm"
                    />
                  </div>
                )}
                {categories.length > 1 && (
                  <button type="button" onClick={() => removeCategory(category.id)} className="ml-4 text-red-500 hover:text-red-700">
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
              {useSimplified && (
                <div className="pl-4">
                  <label className="block text-sm font-medium text-gray-700">Criteria Breakdown Description</label>
                  <textarea
                    rows="3"
                    placeholder="e.g., Score 10 for high scalability, 5 for moderate maintenance cost, 0 for failure."
                    value={category.simplifiedCriteria}
                    onChange={(e) => handleCategoryChange(category.id, 'simplifiedCriteria', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                  />
                </div>
              )}
              {!useSimplified && (
                <div className="space-y-3 pl-4">
                  <h3 className="text-md font-semibold text-gray-600">Detailed Criteria:</h3>
                  {category.criteria?.map((criterion, critIndex) => (
                    <div key={criterion.id} className="grid grid-cols-1 gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 md:grid-cols-4">
                      <input
                        type="text"
                        placeholder={`Criterion ${critIndex + 1} Name (e.g., Scalability)`}
                        value={criterion.name}
                        onChange={(e) => handleCriterionChange(category.id, criterion.id, 'name', e.target.value)}
                        required
                        className="rounded-md border-gray-300 shadow-sm md:col-span-2"
                      />
                      <div>
                        <label className="block text-xs text-gray-500">Max Points</label>
                        <input
                          type="number"
                          min="1"
                          value={criterion.maxPoints}
                          onChange={(e) => handleCriterionChange(category.id, criterion.id, 'maxPoints', e.target.value)}
                          required
                          className="w-full rounded-md border-gray-300 shadow-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">Weight</label>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={criterion.weight}
                          onChange={(e) => handleCriterionChange(category.id, criterion.id, 'weight', e.target.value)}
                          required
                          className="w-full rounded-md border-gray-300 shadow-sm"
                        />
                      </div>
                      {category.criteria.length > 1 && (
                        <button type="button" onClick={() => removeCriterion(category.id, criterion.id)} className="text-red-500 hover:text-red-700 md:col-start-4 md:justify-self-end">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => addCriterion(category.id)} className="flex items-center text-sm text-blue-600 hover:text-blue-800">
                    <Plus className="mr-1 h-4 w-4" /> Add Criterion
                  </button>
                </div>
              )}
            </div>
          ))}
          <button type="button" onClick={addCategory} className="flex items-center rounded-md border border-dashed border-gray-400 px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-500 hover:text-gray-900">
            <Plus className="mr-2 h-5 w-5" /> Add Category
          </button>
        </div>

        {error && <p className="text-sm font-medium text-red-600">{error}</p>}

        {/* Submit Button (No changes) */}
        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="rounded-md border border-transparent bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : isEditing ? 'Update Session' : 'Save Scoring Session'}
            {isEditing && <Check className="ml-2 h-4 w-4" />}
          </button>
        </div>
      </form>
    </div>
  );
}